/**
 * Utility for displaying friendly connector names in the UI.
 * Maps connector IDs (e.g., "connector_microsoftteams") to display names (e.g., "Microsoft Teams").
 */

import {ConnectorIds} from '../constants/ConnectorIds';

// Mapping of connector ID -> friendly display name
const CONNECTOR_DISPLAY_NAMES: Record<string, string> = {
    [ConnectorIds.Dropbox]: 'Dropbox',
    [ConnectorIds.Gmail]: 'Gmail',
    [ConnectorIds.GoogleCalendar]: 'Google Calendar',
    [ConnectorIds.GoogleDrive]: 'Google Drive',
    [ConnectorIds.MicrosoftTeams]: 'Microsoft Teams',
    [ConnectorIds.OutlookCalendar]: 'Outlook Calendar',
    [ConnectorIds.OutlookEmail]: 'Outlook Email',
    [ConnectorIds.SharePoint]: 'SharePoint'
};

/**
 * Get the friendly display name for a connector/server.
 *
 * @param serverLabel - The server_label from the API
 * @param serverName - Optional ServerName field from database
 * @returns The friendly display name
 *
 * @example
 * getDisplayName('connector_microsoftteams', null) // Returns "Microsoft Teams"
 * getDisplayName('my-custom-server', 'My Company Tools') // Returns "My Company Tools"
 * getDisplayName('my-custom-server', null) // Returns "My Custom Server"
 */
export function getDisplayName(
    serverLabel: string,
    serverName?: string | null
): string {
    // If serverName is provided, use it
    if (serverName) {
        return serverName;
    }

    // Otherwise fall back to connector lookup or formatting
    return getConnectorDisplayName(serverLabel);
}

/**
 * Get the friendly display name for a connector/server label.
 * Handles connector IDs (e.g., "connector_microsoftteams") and falls back to formatting if not found.
 *
 * @param serverLabelOrConnectorId - The server_label or connector_id from the API
 * @returns The friendly display name (e.g., "Microsoft Teams")
 *
 * @example
 * getConnectorDisplayName('connector_microsoftteams') // Returns "Microsoft Teams"
 * getConnectorDisplayName('connector_sharepoint') // Returns "SharePoint"
 * getConnectorDisplayName('custom-mcp-server') // Returns "Custom Mcp Server" (formatted fallback)
 */
export function getConnectorDisplayName(
    serverLabelOrConnectorId: string
): string {
    // Try direct lookup first
    const displayName = CONNECTOR_DISPLAY_NAMES[serverLabelOrConnectorId];
    if (displayName) {
        return displayName;
    }

    // Fallback: Format the server label nicely
    // Convert "connector_something" -> "Something"
    // Convert "custom-mcp-server" -> "Custom Mcp Server"
    let formatted = serverLabelOrConnectorId;

    // Remove "connector_" prefix if present
    if (formatted.startsWith('connector_')) {
        formatted = formatted.substring('connector_'.length);
    }

    // Split by hyphens or underscores and capitalize each word
    return formatted
        .split(/[-_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
